import { useState, useCallback, useRef, useEffect, KeyboardEvent } from 'react';
import { Box, InputBase, Stack, Typography, Fade, ClickAwayListener, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { SendOutlined, StopOutlined, SearchOutlined, LockOutlined, CloseOutlined } from '@ant-design/icons';
import IconButton from 'components/@extended/IconButton';
import { TurnState } from '../../hooks/useAiChat';

interface QuickCommand {
  label: string;
  prefill: string;
  icon: React.ReactNode;
  description: string;
  cursorPos?: number;
}

const QUICK_COMMANDS: QuickCommand[] = [
  {
    label: 'Analyze Report',
    prefill: 'Analyze report for application <APP_NUMBER> owner <OWNER>',
    icon: <SearchOutlined />,
    description: 'Replace <APP_NUMBER> and <OWNER> (index 0/1/… or full name), then send',
    cursorPos: 36,
  },
];

interface AiChatInputProps {
  onSend: (message: string) => void;
  onCancel: () => void;
  onEndTurn: () => void;
  turnState: TurnState;
  disabled: boolean;
  prefillText?: string;
  onPrefillConsumed?: () => void;
}

const PLACEHOLDER_TOKENS = ['<APP_NUMBER>', '<OWNER>'] as const;

function findUnresolvedPlaceholder(text: string): string | null {
  for (const token of PLACEHOLDER_TOKENS) {
    if (text.includes(token)) return token;
  }
  return null;
}

function findNextPlaceholder(text: string, fromIdx: number): { token: string; idx: number } | null {
  let best: { token: string; idx: number } | null = null;
  for (const token of PLACEHOLDER_TOKENS) {
    const idx = text.indexOf(token, fromIdx);
    if (idx >= 0 && (!best || idx < best.idx)) best = { token, idx };
  }
  return best;
}

const AiChatInput = ({
  onSend,
  onCancel,
  onEndTurn,
  turnState,
  disabled,
  prefillText,
  onPrefillConsumed,
}: AiChatInputProps) => {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const [showCommands, setShowCommands] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderWarn, setPlaceholderWarn] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isStreaming = turnState === 'streaming';
  const isAwaitingAction = turnState === 'awaiting_action';
  const isLocked = isStreaming || isAwaitingAction || disabled;

  useEffect(() => {
    if (prefillText) {
      setValue(prefillText);
      onPrefillConsumed?.();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const placeholderIdx = prefillText.indexOf('<APP_NUMBER>');
          if (placeholderIdx !== -1) {
            inputRef.current.setSelectionRange(placeholderIdx, placeholderIdx + '<APP_NUMBER>'.length);
          }
        }
      }, 50);
    }
  }, [prefillText, onPrefillConsumed]);

  const handleSend = useCallback(() => {
    if (!value.trim() || isLocked) return;
    const unresolved = findUnresolvedPlaceholder(value);
    if (unresolved) {
      setPlaceholderWarn(unresolved);
      const idx = value.indexOf(unresolved);
      if (inputRef.current && idx >= 0) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(idx, idx + unresolved.length);
      }
      return;
    }
    setPlaceholderWarn(null);
    onSend(value);
    setValue('');
    setShowCommands(false);
  }, [value, isLocked, onSend]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
      return;
    }
    if (e.key === 'Tab' && findUnresolvedPlaceholder(value)) {
      const target = e.target as HTMLInputElement;
      const cursor = (target?.selectionEnd ?? 0);
      const next = findNextPlaceholder(value, cursor) ?? findNextPlaceholder(value, 0);
      if (next && inputRef.current) {
        e.preventDefault();
        inputRef.current.setSelectionRange(next.idx, next.idx + next.token.length);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (showCommands) {
        setShowCommands(false);
        return;
      }
      if (isAwaitingAction) {
        e.preventDefault();
        onEndTurn();
      }
    }
  };

  const handleCommandSelect = (cmd: QuickCommand) => {
    setValue(cmd.prefill);
    setShowCommands(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (cmd.cursorPos !== undefined) {
          inputRef.current.setSelectionRange(cmd.cursorPos, cmd.cursorPos);
        }
      }
    }, 50);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setShowCommands(newValue === '/');
    if (placeholderWarn && !findUnresolvedPlaceholder(newValue)) {
      setPlaceholderWarn(null);
    }
  };

  const isDark = theme.palette.mode === 'dark';

  const placeholder = (() => {
    if (disabled) return 'Disconnected…';
    if (isStreaming) return 'AI is responding…';
    if (isAwaitingAction) return 'Complete the pending step above, or click End turn to cancel';
    return 'Type / for commands, or ask anything…';
  })();

  return (
    <Box sx={{
      position: 'relative',
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
      flexShrink: 0,
      bgcolor: isDark ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
    }}>
      <Fade in={showCommands}>
        <Box sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, zIndex: 1 }}>
          <ClickAwayListener onClickAway={() => setShowCommands(false)}>
            <Box sx={{
              mx: 1.5, mb: 0.75, borderRadius: 2, overflow: 'hidden',
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              boxShadow: `0 -12px 32px ${alpha(theme.palette.common.black, isDark ? 0.5 : 0.12)}`,
            }}>
              <Typography sx={{
                display: 'block', px: 1.75, py: 1,
                color: 'text.disabled',
                fontWeight: 700,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : alpha(theme.palette.background.default, 0.5),
              }}>
                Quick Commands
              </Typography>
              {QUICK_COMMANDS.map((cmd, i) => (
                <Box key={i} onClick={() => handleCommandSelect(cmd)} sx={{
                  px: 1.75, py: 1.25, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'background-color 0.12s ease',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  borderBottom: i < QUICK_COMMANDS.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.4)}` : 'none',
                }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: 1.25, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    fontSize: 15,
                  }}>
                    {cmd.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      lineHeight: 1.35,
                      color: 'text.primary',
                      mb: 0.25,
                    }}>
                      {cmd.label}
                    </Typography>
                    <Typography sx={{
                      color: 'text.secondary',
                      fontSize: '0.8125rem',
                      lineHeight: 1.45,
                    }}>
                      {cmd.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </ClickAwayListener>
        </Box>
      </Fade>

      {placeholderWarn && (
        <Box sx={{
          px: 1.75, pt: 1, pb: 0.5,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: theme.palette.warning.main,
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontSize: '0.75rem',
            color: theme.palette.warning.main,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}>
            Replace <Box component="code" sx={{ fontFamily: 'SF Mono, Menlo, monospace', px: 0.5, mx: 0.25, borderRadius: 0.5, bgcolor: alpha(theme.palette.warning.main, 0.12) }}>{placeholderWarn}</Box> before sending — Tab cycles through placeholders
          </Typography>
        </Box>
      )}

      {isAwaitingAction && (
        <Box sx={{
          px: 1.75, pt: 1, pb: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1.25,
        }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <LockOutlined style={{ fontSize: 12, color: theme.palette.warning.main, flexShrink: 0 }} />
            <Typography sx={{
              fontSize: '0.75rem',
              color: theme.palette.warning.main,
              fontWeight: 600,
              letterSpacing: 0.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              Pending step — resolve above or end turn
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<CloseOutlined style={{ fontSize: 11 }} />}
            onClick={onEndTurn}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              py: 0.25,
              px: 1,
              minWidth: 0,
              textTransform: 'none',
              color: theme.palette.text.secondary,
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 1,
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                borderColor: alpha(theme.palette.warning.main, 0.4),
                color: theme.palette.warning.main,
              },
            }}
          >
            End turn
          </Button>
        </Box>
      )}

      <Box sx={{ px: 1.75, py: 1.5, pt: isAwaitingAction ? 0.75 : 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-end"
          sx={{
            borderRadius: 2.5,
            p: 0.5,
            bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.035),
            border: `1px solid ${isFocused ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.6)}`,
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            boxShadow: isFocused
              ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
              : 'none',
            opacity: isAwaitingAction ? 0.55 : 1,
          }}
        >
          <InputBase
            inputRef={inputRef}
            multiline
            maxRows={5}
            fullWidth
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={isLocked}
            sx={{
              px: 1.5,
              py: 1,
              fontSize: '0.9375rem',
              lineHeight: 1.5,
              color: 'text.primary',
              '& .MuiInputBase-input::placeholder': {
                opacity: 0.55,
                fontSize: '0.9375rem',
              },
              '& .MuiInputBase-input:disabled': {
                WebkitTextFillColor: theme.palette.text.disabled,
              },
            }}
          />
          {isStreaming ? (
            <IconButton
              color="error"
              onClick={onCancel}
              sx={{
                width: 38, height: 38, flexShrink: 0, mb: 0.25,
                bgcolor: alpha(theme.palette.error.main, 0.14),
                color: theme.palette.error.main,
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.22) },
              }}
            >
              <StopOutlined style={{ fontSize: 17 }} />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              disabled={!value.trim() || isLocked}
              onClick={handleSend}
              sx={{
                width: 38, height: 38, flexShrink: 0, mb: 0.25,
                bgcolor: value.trim() && !isLocked ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.14),
                color: value.trim() && !isLocked ? '#fff' : theme.palette.primary.main,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: value.trim() && !isLocked ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.22),
                },
                '&.Mui-disabled': {
                  bgcolor: alpha(theme.palette.text.disabled, 0.1),
                  color: alpha(theme.palette.text.disabled, 0.5),
                },
              }}
            >
              <SendOutlined style={{ fontSize: 17 }} />
            </IconButton>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75, px: 0.5 }}>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', letterSpacing: 0.2 }}>
            <Box component="kbd" sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 18, height: 16, px: 0.5,
              borderRadius: 0.5,
              bgcolor: alpha(theme.palette.text.disabled, 0.12),
              color: 'text.secondary',
              fontFamily: 'SF Mono, Menlo, Consolas, monospace',
              fontSize: '0.6875rem', fontWeight: 600,
              mr: 0.5,
            }}>↵</Box>
            to send · Shift+↵ new line{isAwaitingAction ? ' · Esc end turn' : ''}
          </Typography>
          {value.length > 0 && (
            <Typography sx={{
              fontSize: '0.6875rem',
              color: value.length > 2000 ? theme.palette.warning.main : 'text.disabled',
              fontFamily: 'SF Mono, Menlo, Consolas, monospace',
              fontWeight: 500,
            }}>
              {value.length}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default AiChatInput;
